import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Server,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
  Globe,
  Lock,
  Tag,
  ArrowRight,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Layers,
  List,
  Forward,
  RefreshCw,
  Wand2,
  Sparkles,
  Key,
} from "lucide-react";
import { api, Source, Domain, SourceDailyStat, SourceMetadata, DailyDetail } from "@/lib/api";
import { Modal } from "@/components/ui/Modal";
import { SourceLabelDialog } from "@/components/sources/SourceLabelDialog";
import { BulkLabelDialog } from "@/components/sources/BulkLabelDialog";
import { TaskGenerationLink } from "@/components/sources/TaskGenerationLink";
import { SourceProviderGroup } from "@/components/sources/SourceProviderGroup";
import { SourceDetailsPanel } from "@/components/sources/SourceDetailsPanel";
import { ProviderHealthCard, computeEnforcementReadiness } from "@/components/sources/ProviderHealthCard";
import { BulkExportBar } from "@/components/sources/BulkExportBar";
import { SigningDomainsView } from "@/components/sources/SigningDomainsView";
import {
  UpgradePrompt,
  LockedFeatureButton,
} from "@/components/upgrade/UpgradePrompt";
import { useDataStore } from "@/stores/data";
import { useAuthStore } from "@/stores/auth";
import { useFeatureDiscovery } from "@/lib/ux-analytics";
import { toast } from "@/lib/toast";
import { groupSourcesByProvider } from "@/lib/source-grouping";

type StatusFilter = "all" | "authorized" | "pending" | "unauthorized" | "suspicious";
type LabelFilter = "all" | "authorized" | "customer_support" | "cicd" | "forwarder" | "unauthorized" | "investigating" | "unlabeled";

// Label category display names
const LABEL_OPTIONS: { value: LabelFilter; label: string }[] = [
  { value: "all", label: "All Labels" },
  { value: "unlabeled", label: "Unlabeled" },
  { value: "authorized", label: "Authorized" },
  { value: "customer_support", label: "Customer Support" },
  { value: "cicd", label: "CI/CD" },
  { value: "forwarder", label: "Forwarder" },
  { value: "unauthorized", label: "Unauthorized" },
  { value: "investigating", label: "Investigating" },
];

// Status filter display names
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All Status" },
  { value: "authorized", label: "Authorized" },
  { value: "pending", label: "Pending Review" },
  { value: "unauthorized", label: "Unauthorized" },
  { value: "suspicious", label: "Suspicious" },
];

// Helper to determine if a source is an unknown IP (no hostname and no known provider)
function isUnknownIP(source: Source): boolean {
  const hasHostname = !!source.ptr_hostname;
  const hasKnownProvider = !!(source.org_name || source.provider_name || source.source_name);
  return !hasHostname && !hasKnownProvider;
}

// Helper to determine if a source is suspicious based on auth failures
// A source is suspicious if it has no known provider AND is failing both SPF and DKIM
function isSuspiciousSource(source: Source): boolean {
  const hasKnownProvider = !!(source.provider_name);
  if (hasKnownProvider) return false;

  // Check for auth failures - 0% or very low pass rates with some volume
  const spfPassRate = source.spf_pass_rate || 0;
  const dkimPassRate = source.dkim_pass_rate || 0;
  const hasVolume = (source.total_message_count || 0) > 0;

  // Suspicious: no known provider AND both SPF and DKIM failing
  if (hasVolume && spfPassRate < 0.05 && dkimPassRate < 0.05) {
    return true;
  }

  // Also suspicious: completely unknown (no hostname, no provider, no org)
  if (isUnknownIP(source)) {
    return true;
  }

  return false;
}

// Helper to determine if a source is a forwarder
function isForwarder(source: Source): boolean {
  // Check source_type for forwarder variants
  const forwarderTypes = ["forwarder", "mailing_list", "trusted_forwarder", "arc_forwarder"];
  if (source.source_type && forwarderTypes.includes(source.source_type)) {
    return true;
  }
  // Also check forwarding_detected flag
  if (source.forwarding_detected) {
    return true;
  }
  // Check label_category
  if (source.label_category === "forwarder") {
    return true;
  }
  return false;
}

// Helper to get the display status based on labeling system rules
function getSourceStatus(source: Source): "authorized" | "unauthorized" | "pending_review" | "suspicious" {
  if (source.authorization_status === "authorized") {
    return "authorized";
  }
  if (source.authorization_status === "unauthorized") {
    return "unauthorized";
  }
  // Flag as suspicious if: unknown IP OR (no provider AND failing auth)
  if (isSuspiciousSource(source)) {
    return "suspicious";
  }
  return "pending_review";
}

// Status badge component
function StatusBadge({ status }: { status: "authorized" | "unauthorized" | "pending_review" | "suspicious" }) {
  const config = {
    authorized: {
      label: "Authorized",
      icon: CheckCircle2,
      className: "bg-emerald-100 text-emerald-700",
    },
    unauthorized: {
      label: "Unauthorized",
      icon: XCircle,
      className: "bg-red-100 text-red-700",
    },
    pending_review: {
      label: "Pending Review",
      icon: AlertTriangle,
      className: "bg-orange-100 text-orange-700",
    },
    suspicious: {
      label: "Suspicious",
      icon: HelpCircle,
      className: "bg-purple-100 text-purple-600",
    },
  };

  const { label, icon: Icon, className } = config[status];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium w-fit ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// AI Classification badge - shows when AI has analyzed the source
function AIClassificationBadge({ source }: { source: Source }) {
  // Only show if AI has analyzed this source
  if (!source.ai_analyzed_at) return null;

  // Determine badge state
  const needsApproval = source.auto_labeled && !source.user_confirmed;
  const confidence = source.ai_confidence || "low";

  const confidenceColors = {
    high: "bg-emerald-100 text-emerald-700 border-emerald-200",
    medium: "bg-amber-100 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-600 border-slate-200",
  };

  if (needsApproval) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-600 border border-purple-200 rounded text-xs w-fit animate-pulse">
        <Sparkles className="w-3 h-3" />
        AI Review
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs w-fit border ${confidenceColors[confidence]}`}
      title={source.ai_reasoning || `AI classified with ${confidence} confidence`}
    >
      <Sparkles className="w-3 h-3" />
      AI {confidence}
    </span>
  );
}

interface Permissions {
  canViewHostname: boolean;
  canLabelSources: boolean;
  canViewRemediation: boolean;
  canApplyRemediation: boolean;
}

// Provider grouping moved to @/lib/source-grouping (shared with ApexLinkWizard).

// CSV export helper
function downloadCsv(headers: string[], rows: string[][], filenamePrefix: string) {
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell || "").replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filenamePrefix}-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function Sources() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeClientOrg } = useAuthStore();

  // Track feature discovery
  useFeatureDiscovery("sources");

  // Initialize domain filter from URL param (supports deep linking from Domain page)
  const initialDomain = searchParams.get("domain") || "all";
  const initialView = searchParams.get("view");

  const [sources, setSources] = useState<Source[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [labelFilter, setLabelFilter] = useState<LabelFilter>("all");
  const [selectedDomain, setSelectedDomain] = useState<string>(initialDomain);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Tier and permissions
  const [tier, setTier] = useState<string>("free");
  const [permissions, setPermissions] = useState<Permissions>({
    canViewHostname: false,
    canLabelSources: false,
    canViewRemediation: false,
    canApplyRemediation: false,
  });

  // Dialog state
  const [labelDialogSource, setLabelDialogSource] = useState<Source | null>(null);
  const [showBulkLabelDialog, setShowBulkLabelDialog] = useState(false);
  const [upgradePromptFeature, setUpgradePromptFeature] = useState<string | null>(null);

  // New state for grouping, expansion, and selection
  type ViewMode = "grouped" | "list" | "signing_domains";
  const [viewMode, setViewMode] = useState<ViewMode>(
    initialView === "signing_domains" ? "signing_domains" : initialView === "list" ? "list" : "grouped"
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  const [sourceStats, setSourceStats] = useState<Record<string, SourceDailyStat[]>>({});
  const [sourceMetadata, setSourceMetadata] = useState<Record<string, SourceMetadata>>({});
  const [loadingStats, setLoadingStats] = useState<Set<string>>(new Set());
  const [providerDailyDetail, setProviderDailyDetail] = useState<Record<string, DailyDetail[]>>({});
  const [loadingProviderStats, setLoadingProviderStats] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [autoLabeling, setAutoLabeling] = useState(false);
  const [autoLabelMessage, setAutoLabelMessage] = useState<string | null>(null);
  const [showAutoLabelConfirm, setShowAutoLabelConfirm] = useState(false);
  const [autoLabelPreview, setAutoLabelPreview] = useState<{
    totalSources: number;
    unlabeledSources: number;
    withKnownProvider: number;
    detectedForwarders: number;
    estimatedAutoLabeled: number;
  } | null>(null);

  // Sync domain filter to URL for deep linking
  useEffect(() => {
    const currentDomain = searchParams.get("domain") || "all";
    if (selectedDomain !== currentDomain) {
      if (selectedDomain === "all") {
        // Remove param when filter is cleared
        searchParams.delete("domain");
      } else {
        searchParams.set("domain", selectedDomain);
      }
      setSearchParams(searchParams, { replace: true });
    }
  }, [selectedDomain, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        setLoading(true);

        const store = useDataStore.getState();
        const [domainsResult, sourcesResult] = await Promise.all([
          store.ensureDomains(),
          store.ensureSources(),
        ]);

        if (cancelled) return;

        const freshStore = useDataStore.getState();
        setDomains(domainsResult);
        setTier(freshStore.sourcesTier);
        setPermissions(freshStore.sourcesPermissions);

        setSources(
          sourcesResult.map((s) => ({
            ...s,
            domainName: s.domain_name,
          }))
        );

        // Debug: Log forwarder-related fields
        const forwarderCount = sourcesResult.filter((s) => {
          const forwarderTypes = ["forwarder", "mailing_list", "trusted_forwarder", "arc_forwarder"];
          return (
            (s.source_type && forwarderTypes.includes(s.source_type)) ||
            s.forwarding_detected ||
            s.label_category === "forwarder"
          );
        }).length;
        console.log(`[Sources] Loaded ${sourcesResult.length} sources, ${forwarderCount} forwarders detected`);
        if (forwarderCount === 0 && sourcesResult.length > 0) {
          // Log sample of source_type values to help debug
          const sampleTypes = sourcesResult.slice(0, 5).map((s) => ({
            id: s.id.slice(0, 8),
            source_type: s.source_type,
            forwarding_detected: s.forwarding_detected,
            label_category: s.label_category,
          }));
          console.log("[Sources] Sample source_type values:", sampleTypes);
        }

        // Start with all groups collapsed by default
        setExpandedGroups(new Set());
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch sources:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    const handleClientChange = () => {
      fetchData();
    };
    window.addEventListener("msp-client-changed", handleClientChange);

    return () => {
      cancelled = true;
      window.removeEventListener("msp-client-changed", handleClientChange);
    };
  }, [activeClientOrg?.id]);

  // Fetch stats for a source when expanded
  const fetchSourceStats = useCallback(async (sourceId: string) => {
    if (sourceStats[sourceId] || loadingStats.has(sourceId)) {
      return; // Already fetched or loading
    }

    setLoadingStats((prev) => new Set([...prev, sourceId]));

    try {
      const result = await api.getSourceStatsBatch([sourceId], 30);
      setSourceStats((prev) => ({
        ...prev,
        [sourceId]: result.stats[sourceId] || [],
      }));
      // Also store metadata (DKIM selectors, envelope_from domains)
      if (result.metadata && result.metadata[sourceId]) {
        setSourceMetadata((prev) => ({
          ...prev,
          [sourceId]: result.metadata![sourceId]!,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch source stats:", error);
      setSourceStats((prev) => ({
        ...prev,
        [sourceId]: [],
      }));
    } finally {
      setLoadingStats((prev) => {
        const next = new Set(prev);
        next.delete(sourceId);
        return next;
      });
    }
  }, [sourceStats, loadingStats]);

  // Toggle source expansion
  const handleToggleExpand = useCallback((sourceId: string) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
        // Fetch stats when expanding
        fetchSourceStats(sourceId);
      }
      return next;
    });
  }, [fetchSourceStats]);

  // Toggle source selection
  const handleToggleSelect = useCallback((sourceId: string) => {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  }, []);

  // Select all sources in a group
  const handleSelectGroup = useCallback((groupSources: Source[], selected: boolean) => {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      for (const source of groupSources) {
        if (selected) {
          next.add(source.id);
        } else {
          next.delete(source.id);
        }
      }
      return next;
    });
  }, []);

  // Fetch provider-level daily detail (aggregated across all IPs in group)
  const fetchProviderGroupStats = useCallback(async (groupKey: string, groupSources: Source[]) => {
    if (providerDailyDetail[groupKey] || loadingProviderStats.has(groupKey)) {
      return;
    }

    const sourceIds = groupSources.map((s) => s.id);
    if (sourceIds.length === 0) return;

    setLoadingProviderStats((prev) => new Set([...prev, groupKey]));

    try {
      const result = await api.getSourceStatsBatch(sourceIds, 30);

      // Aggregate daily_detail across all sources, preserving all fields
      const dayMap = new Map<string, {
        date: string;
        total_messages: number;
        spf_pass: number;
        spf_fail: number;
        dmarc_pass: number;
        dmarc_fail: number;
        disposition_none: number;
        disposition_quarantine: number;
        disposition_reject: number;
        header_from: Set<string>;
        envelope_from: Set<string>;
        selectors: Map<string, { pass: number; fail: number; total: number; dkim_domain?: string | null }>;
      }>();

      if (result.metadata) {
        for (const sourceId of sourceIds) {
          const meta = result.metadata[sourceId];
          if (!meta?.daily_detail) continue;

          for (const day of meta.daily_detail) {
            let entry = dayMap.get(day.date);
            if (!entry) {
              entry = {
                date: day.date,
                total_messages: 0,
                spf_pass: 0,
                spf_fail: 0,
                dmarc_pass: 0,
                dmarc_fail: 0,
                disposition_none: 0,
                disposition_quarantine: 0,
                disposition_reject: 0,
                header_from: new Set(),
                envelope_from: new Set(),
                selectors: new Map(),
              };
              dayMap.set(day.date, entry);
            }
            entry.total_messages += day.total_messages;
            entry.spf_pass += day.spf_pass;
            entry.spf_fail += day.spf_fail;
            entry.dmarc_pass += day.dmarc_pass ?? 0;
            entry.dmarc_fail += day.dmarc_fail ?? 0;
            entry.disposition_none += day.disposition_none ?? 0;
            entry.disposition_quarantine += day.disposition_quarantine ?? 0;
            entry.disposition_reject += day.disposition_reject ?? 0;
            for (const hf of day.header_from ?? []) entry.header_from.add(hf);
            for (const ef of day.envelope_from ?? []) entry.envelope_from.add(ef);

            for (const sel of day.selectors) {
              const existing = entry.selectors.get(sel.selector);
              if (existing) {
                existing.pass += sel.pass;
                existing.fail += sel.fail;
                existing.total += sel.total;
                if (sel.dkim_domain && !existing.dkim_domain) {
                  existing.dkim_domain = sel.dkim_domain;
                }
              } else {
                entry.selectors.set(sel.selector, { ...sel });
              }
            }
          }
        }
      }

      const aggregated: DailyDetail[] = Array.from(dayMap.values())
        .map((d) => ({
          date: d.date,
          total_messages: d.total_messages,
          spf_pass: d.spf_pass,
          spf_fail: d.spf_fail,
          dmarc_pass: d.dmarc_pass,
          dmarc_fail: d.dmarc_fail,
          disposition_none: d.disposition_none,
          disposition_quarantine: d.disposition_quarantine,
          disposition_reject: d.disposition_reject,
          header_from: Array.from(d.header_from),
          envelope_from: Array.from(d.envelope_from),
          selectors: Array.from(d.selectors.entries())
            .map(([selector, stats]) => ({ selector, ...stats }))
            .sort((a, b) => b.total - a.total),
        }))
        .sort((a, b) => b.date.localeCompare(a.date));

      setProviderDailyDetail((prev) => ({
        ...prev,
        [groupKey]: aggregated,
      }));

      // Also store individual source stats/metadata for when individual IPs are expanded
      for (const sourceId of sourceIds) {
        if (result.stats[sourceId]) {
          setSourceStats((prev) => prev[sourceId] ? prev : ({ ...prev, [sourceId]: result.stats[sourceId] || [] }));
        }
        if (result.metadata?.[sourceId]) {
          setSourceMetadata((prev) => prev[sourceId] ? prev : ({ ...prev, [sourceId]: result.metadata![sourceId]! }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch provider group stats:", error);
    } finally {
      setLoadingProviderStats((prev) => {
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
    }
  }, [providerDailyDetail, loadingProviderStats]);

  // Toggle group expansion
  const handleToggleGroup = useCallback((groupName: string, groupSources?: Source[]) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
        if (groupSources) {
          fetchProviderGroupStats(groupName, groupSources);
        }
      }
      return next;
    });
  }, [fetchProviderGroupStats]);

  // Export selected sources to CSV
  const handleExport = useCallback(() => {
    const selectedList = sources.filter((s) => selectedSources.has(s.id));
    if (selectedList.length === 0) return;

    const headers = [
      "IP Address",
      "Hostname",
      "Provider",
      "Organization",
      "Domain",
      "Total Messages",
      "SPF Pass Rate",
      "DKIM Pass Rate",
      "Status",
      "Label",
      "First Seen",
      "Last Seen",
    ];

    const rows = selectedList.map((s) => [
      s.source_ip || s.identifier,
      s.ptr_hostname || "",
      s.provider_name || "",
      s.org_name || "",
      s.domain_name || "",
      String(s.total_message_count),
      `${Math.round((s.spf_pass_rate || 0) * 100)}%`,
      `${Math.round((s.dkim_pass_rate || 0) * 100)}%`,
      getSourceStatus(s),
      s.label_category ? formatLabel(s.label_category, s.label_subtype) : "",
      s.first_seen_at,
      s.last_seen_at,
    ]);

    downloadCsv(headers, rows, "sources-export");
    setSelectedSources(new Set());
  }, [sources, selectedSources]);

  // Bulk label selected sources
  const handleBulkLabel = useCallback(async (
    category: string,
    subtype: string,
    notes?: string
  ) => {
    const sourceIds = Array.from(selectedSources);
    if (sourceIds.length === 0) return;

    const result = await api.bulkLabelSources(sourceIds, category, subtype, notes);

    if (result.success) {
      // Determine new authorization status
      const authorizedCategories = ["authorized", "customer_support", "cicd"];
      const unauthorizedCategories = ["unauthorized"];
      let newAuthStatus: "authorized" | "unauthorized" | "pending_review" = "pending_review";
      if (authorizedCategories.includes(category)) {
        newAuthStatus = "authorized";
      } else if (unauthorizedCategories.includes(category)) {
        newAuthStatus = "unauthorized";
      }

      // Update local state
      setSources(prevSources =>
        prevSources.map(s =>
          selectedSources.has(s.id)
            ? {
                ...s,
                label_category: category,
                label_subtype: subtype,
                authorization_status: newAuthStatus,
              }
            : s
        )
      );

      // Invalidate cache
      const store = useDataStore.getState();
      store.invalidateSources();

      // Clear selection
      setSelectedSources(new Set());
    }
  }, [selectedSources]);

  // Handle bulk label button click
  const handleBulkLabelClick = useCallback(() => {
    if (permissions.canLabelSources) {
      setShowBulkLabelDialog(true);
    } else {
      setUpgradePromptFeature("source_labeling");
    }
  }, [permissions.canLabelSources]);

  // Sync: backfill missing sources from report records, then recalculate pass rates
  const handleSyncData = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncMessage(null);
    try {
      // Step 1: Backfill missing sources from dmarc_report_records
      const backfill = await api.backfillSources();
      const backfillMsg = backfill.created > 0
        ? `Found ${backfill.created} new sources. `
        : "";

      // Step 2: Recalculate pass rates for all sources
      const result = await api.recalculatePassRates();

      if (result.success) {
        setSyncMessage(`${backfillMsg}Synced ${result.rowsUpdated} source(s)`);
        // Refresh sources data
        const store = useDataStore.getState();
        store.invalidateSources();
        const refreshedSources = await store.ensureSources();
        setSources(
          refreshedSources.map((s) => ({
            ...s,
            domainName: s.domain_name,
          }))
        );
        // Clear message after 5 seconds (longer to read backfill info)
        setTimeout(() => setSyncMessage(null), 5000);
      }
    } catch (error) {
      console.error("Failed to sync data:", error);
      toast.error("Failed to sync data");
    } finally {
      setSyncing(false);
    }
  }, [syncing]);

  // Show auto-label confirmation dialog with preview
  const handleAutoLabelClick = useCallback(async () => {
    if (autoLabeling) return;
    try {
      // Get preview of what would be auto-labeled
      const preview = await api.getAutoLabelPreview(selectedDomain !== "all" ? selectedDomain : undefined);
      setAutoLabelPreview(preview);
      setShowAutoLabelConfirm(true);
    } catch (error) {
      console.error("Failed to get auto-label preview:", error);
      toast.error("Failed to analyze sources");
    }
  }, [autoLabeling, selectedDomain]);

  // Execute auto-labeling
  const handleAutoLabel = useCallback(async () => {
    if (autoLabeling) return;
    setAutoLabeling(true);
    setAutoLabelMessage(null);
    setShowAutoLabelConfirm(false);
    try {
      const result = await api.autoLabelSources({
        domainId: selectedDomain !== "all" ? selectedDomain : undefined,
        dryRun: false,
        onlyUnlabeled: true,
      });
      if (result.success) {
        const { summary } = result;
        setAutoLabelMessage(
          `Auto-labeled ${summary.updatedCount} sources: ${summary.autoAuthorized} authorized, ${summary.flaggedSuspicious} suspicious, ${summary.needsReview} need review`
        );
        // Refresh sources data
        const store = useDataStore.getState();
        store.invalidateSources();
        const refreshedSources = await store.ensureSources();
        setSources(
          refreshedSources.map((s) => ({
            ...s,
            domainName: s.domain_name,
          }))
        );
        // Clear message after 5 seconds
        setTimeout(() => setAutoLabelMessage(null), 5000);
      }
    } catch (error) {
      console.error("Failed to auto-label sources:", error);
      toast.error("Auto-labeling failed");
    } finally {
      setAutoLabeling(false);
      setAutoLabelPreview(null);
    }
  }, [autoLabeling, selectedDomain]);

  async function handleLabelSource(
    category: string,
    subtype: string,
    notes?: string
  ) {
    if (!labelDialogSource) return;

    const result = await api.labelSource(
      labelDialogSource.id,
      category,
      subtype,
      notes
    );

    const approvedCategories = ["authorized", "customer_support", "cicd"];
    const isApproved = approvedCategories.includes(category);

    const updatedSource: Source = {
      ...labelDialogSource,
      label_category: category,
      label_subtype: subtype,
      approved: isApproved,
      authorization_status: isApproved ? "authorized" : category === "unauthorized" ? "unauthorized" : "pending_review",
    };

    setSources(
      sources.map((s) =>
        s.id === labelDialogSource.id ? updatedSource : s
      )
    );

    const store = useDataStore.getState();
    store.updateSource(updatedSource);
    store.invalidateSources();

    setLabelDialogSource(null);

    if (result.tasksGenerating) {
      window.dispatchEvent(new CustomEvent("source-labeled"));
    }
  }

  function handleLabelClick(source: Source) {
    if (permissions.canLabelSources) {
      setLabelDialogSource(source);
    } else {
      setUpgradePromptFeature("source_labeling");
    }
  }

  async function handleAuthorizeProvider(providerId: string) {
    const domainId = selectedDomain !== "all" ? selectedDomain : null;
    if (!domainId) {
      toast.error("Please select a specific domain to authorize a provider");
      return;
    }
    try {
      await api.authorizeProvider(providerId, domainId, "authorized", "authorized_sender");
      toast.success("Provider authorized");

      // Update local state - mark all sources from this provider as authorized
      setSources(prevSources =>
        prevSources.map(s =>
          s.matched_provider_id === providerId && s.domain_id === domainId
            ? { ...s, authorization_status: "authorized" as const, label_category: "authorized", label_subtype: "authorized_sender", approved: true }
            : s
        )
      );

      // Invalidate and refresh
      const store = useDataStore.getState();
      store.invalidateSources();
    } catch {
      toast.error("Failed to authorize provider");
    }
  }

  function handleUpgrade() {
    navigate("/settings/billing");
    setUpgradePromptFeature(null);
  }

  const filteredSources = sources.filter((source) => {
    const sourceIp = source.source_ip || source.identifier;
    const hostname = source.ptr_hostname || "";
    const orgName = source.org_name || source.provider_name || "";

    const matchesSearch =
      sourceIp.toLowerCase().includes(searchQuery.toLowerCase()) ||
      hostname.toLowerCase().includes(searchQuery.toLowerCase()) ||
      orgName.toLowerCase().includes(searchQuery.toLowerCase());

    const computedStatus = getSourceStatus(source);

    let matchesStatus = true;
    if (statusFilter === "authorized") {
      matchesStatus = computedStatus === "authorized";
    } else if (statusFilter === "pending") {
      matchesStatus = computedStatus === "pending_review";
    } else if (statusFilter === "unauthorized") {
      matchesStatus = computedStatus === "unauthorized";
    } else if (statusFilter === "suspicious") {
      matchesStatus = computedStatus === "suspicious";
    }

    let matchesLabel = true;
    if (labelFilter === "unlabeled") {
      matchesLabel = !source.label_category;
    } else if (labelFilter !== "all") {
      matchesLabel = source.label_category === labelFilter;
    }

    const matchesDomain =
      selectedDomain === "all" || source.domain_id === selectedDomain;

    return matchesSearch && matchesStatus && matchesLabel && matchesDomain;
  });

  // Separate forwarders from regular sources
  const regularSources = filteredSources.filter((s) => !isForwarder(s));
  const forwarderSources = filteredSources.filter((s) => isForwarder(s));

  // Group filtered sources by provider (provider-first model)
  const { providers: providerGroups, unknownSources } = viewMode === "grouped"
    ? groupSourcesByProvider(regularSources, isForwarder)
    : { providers: [{ key: "all", providerId: null, providerName: "All Sources", providerCategory: null, sources: regularSources, totalVolume: 0, weightedSpfRate: 0, weightedDkimRate: 0, effectiveStatus: "authorized" as const, ipCount: regularSources.length }], unknownSources: [] };

  // Group forwarders by provider
  const { providers: forwarderGroups, unknownSources: unknownForwarders } = viewMode === "grouped"
    ? groupSourcesByProvider(forwarderSources, isForwarder)
    : { providers: [{ key: "all-fwd", providerId: null, providerName: "All Forwarders", providerCategory: null, sources: forwarderSources, totalVolume: 0, weightedSpfRate: 0, weightedDkimRate: 0, effectiveStatus: "authorized" as const, ipCount: forwarderSources.length }], unknownSources: [] };

  // Combine all unknown sources
  const allUnknownSources = [...unknownSources, ...unknownForwarders];

  const statusCounts: Record<StatusFilter, number> = {
    all: sources.length,
    authorized: sources.filter((s) => getSourceStatus(s) === "authorized").length,
    pending: sources.filter((s) => getSourceStatus(s) === "pending_review").length,
    unauthorized: sources.filter((s) => getSourceStatus(s) === "unauthorized").length,
    suspicious: sources.filter((s) => getSourceStatus(s) === "suspicious").length,
  };

  const labelCounts: Record<LabelFilter, number> = {
    all: sources.length,
    unlabeled: sources.filter((s) => !s.label_category).length,
    authorized: sources.filter((s) => s.label_category === "authorized").length,
    customer_support: sources.filter((s) => s.label_category === "customer_support").length,
    cicd: sources.filter((s) => s.label_category === "cicd").length,
    forwarder: sources.filter((s) => s.label_category === "forwarder").length,
    unauthorized: sources.filter((s) => s.label_category === "unauthorized").length,
    investigating: sources.filter((s) => s.label_category === "investigating").length,
  };

  const hasActiveFilters = statusFilter !== "all" || labelFilter !== "all" || selectedDomain !== "all";

  const clearAllFilters = () => {
    setStatusFilter("all");
    setLabelFilter("all");
    setSelectedDomain("all");
    setSearchQuery("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Task Generation Link */}
      <TaskGenerationLink />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800">Sending Sources</h1>
          <p className="text-sm md:text-base text-slate-500">
            Label and manage IPs that send email on behalf of your domains. Mark sources as authorized or unauthorized to track your DMARC compliance.
          </p>
        </div>

        {tier === "free" && (
          <div className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1 md:py-1.5 bg-amber-50 border border-amber-200 rounded-lg self-start">
            <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 text-amber-600" />
            <span className="text-xs md:text-sm text-amber-700">
              Free tier: Limited visibility
            </span>
            <button
              onClick={() => setUpgradePromptFeature("hostname_view")}
              className="text-xs md:text-sm font-medium text-amber-700 hover:text-amber-800 flex items-center gap-0.5 md:gap-1"
            >
              Upgrade
              <ArrowRight className="w-2.5 h-2.5 md:w-3 md:h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 md:p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-slate-400" />
            <input
              type="text"
              placeholder={
                permissions.canViewHostname
                  ? "Search by IP, hostname, or organization..."
                  : "Search by IP address..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 md:pl-10 pr-4 py-2 text-sm md:text-base border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Domain Selector - Always visible for quick filtering */}
          {domains.length > 1 && (
            <div className="relative min-w-[180px]">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className={`w-full pl-9 pr-8 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white appearance-none cursor-pointer ${
                  selectedDomain !== "all"
                    ? "border-amber-300 bg-amber-50 text-amber-700"
                    : "border-slate-200 text-slate-700"
                }`}
              >
                <option value="all">All Domains ({domains.length})</option>
                {domains.map((domain) => (
                  <option key={domain.id} value={domain.id}>
                    {domain.domain_name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          )}

          {/* View toggle */}
          <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => setViewMode("grouped")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "grouped"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Layers className="w-4 h-4" />
              Grouped
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "list"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => setViewMode("signing_domains")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "signing_domains"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Key className="w-4 h-4" />
              Signing Domains
            </button>
          </div>

          {/* Filter toggle button */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showAdvancedFilters || hasActiveFilters
                ? "bg-amber-100 text-amber-700 border border-amber-300"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent"
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {(statusFilter !== "all" ? 1 : 0) + (labelFilter !== "all" ? 1 : 0) + (selectedDomain !== "all" ? 1 : 0)}
              </span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${showAdvancedFilters ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="pt-3 border-t border-slate-200">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Domain</label>
                <select
                  value={selectedDomain}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                >
                  <option value="all">All Domains ({sources.length})</option>
                  {domains.map((domain) => (
                    <option key={domain.id} value={domain.id}>
                      {domain.domain_name} ({sources.filter(s => s.domain_id === domain.id).length})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Authorization Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({statusCounts[option.value]})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Label Category</label>
                <select
                  value={labelFilter}
                  onChange={(e) => setLabelFilter(e.target.value as LabelFilter)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                >
                  {LABEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({labelCounts[option.value]})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-500">Active filters:</span>

                {statusFilter !== "all" && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                    Status: {STATUS_OPTIONS.find(o => o.value === statusFilter)?.label}
                    <button onClick={() => setStatusFilter("all")} className="hover:text-amber-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                {labelFilter !== "all" && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                    Label: {LABEL_OPTIONS.find(o => o.value === labelFilter)?.label}
                    <button onClick={() => setLabelFilter("all")} className="hover:text-amber-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                {selectedDomain !== "all" && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                    Domain: {domains.find(d => d.id === selectedDomain)?.domain_name}
                    <button onClick={() => setSelectedDomain("all")} className="hover:text-amber-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}

                <button
                  onClick={clearAllFilters}
                  className="text-xs text-slate-500 hover:text-slate-700 ml-auto"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <div className="flex items-center gap-4">
          <span>
            Showing {filteredSources.length} of {sources.length} sources
            {viewMode === "grouped" && ` in ${providerGroups.length} sender${providerGroups.length !== 1 ? "s" : ""}`}
            {allUnknownSources.length > 0 && (
              <span className="text-amber-600"> + {allUnknownSources.length} unidentified</span>
            )}
            {forwarderSources.length > 0 && (
              <span className="text-blue-500"> + {forwarderSources.length} forwarder{forwarderSources.length !== 1 ? "s" : ""}</span>
            )}
          </span>
          {/* Sync button */}
          <button
            onClick={handleSyncData}
            disabled={syncing}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-50"
            title="Sync volume data from report records"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync Data"}
          </button>
          {syncMessage && (
            <span className="text-xs text-emerald-700">{syncMessage}</span>
          )}
          {/* Auto-Label button */}
          {permissions.canLabelSources && (
            <button
              onClick={handleAutoLabelClick}
              disabled={autoLabeling}
              className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors disabled:opacity-50"
              title="Automatically label sources based on provider and authentication data"
            >
              <Sparkles className={`w-3.5 h-3.5 ${autoLabeling ? "animate-pulse" : ""}`} />
              {autoLabeling ? "Analyzing..." : "Auto-Label"}
            </button>
          )}
          {autoLabelMessage && (
            <span className="text-xs text-purple-600">{autoLabelMessage}</span>
          )}
        </div>
        {hasActiveFilters && !showAdvancedFilters && (
          <button
            onClick={clearAllFilters}
            className="text-amber-600 hover:text-amber-700 font-medium"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Signing Domains View */}
      {viewMode === "signing_domains" ? (
        <SigningDomainsView
          domainId={selectedDomain !== "all" ? selectedDomain : domains[0]?.id || ""}
          domainName={selectedDomain !== "all" ? domains.find(d => d.id === selectedDomain)?.domain_name || "" : domains[0]?.domain_name || ""}
          needsDomainSelection={selectedDomain === "all" && domains.length > 1}
          canView={permissions.canViewHostname}
        />
      ) : (
      <>
      {/* Sources List */}
      {filteredSources.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 md:p-12 text-center">
          <Server className="w-12 h-12 md:w-16 md:h-16 text-slate-300 mx-auto mb-3 md:mb-4" />
          <h3 className="text-base md:text-lg font-semibold text-slate-800 mb-2">
            {sources.length === 0 ? "No sources detected" : "No matching sources"}
          </h3>
          <p className="text-sm md:text-base text-slate-500">
            {sources.length === 0
              ? "Sources will appear here once DMARC reports are received."
              : "Try adjusting your search or filter criteria."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {providerGroups.map((group) => (
            <div key={group.key}>
              {/* Provider Group Header */}
              {viewMode === "grouped" && (
                <SourceProviderGroup
                  providerName={group.providerName}
                  providerId={group.providerId}
                  providerCategory={group.providerCategory}
                  effectiveStatus={group.effectiveStatus}
                  sources={group.sources}
                  expanded={expandedGroups.has(group.key)}
                  onToggle={() => handleToggleGroup(group.key, group.sources)}
                  selectedCount={group.sources.filter((s) => selectedSources.has(s.id)).length}
                  onSelectAll={(selected) => handleSelectGroup(group.sources, selected)}
                  showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                  onAuthorize={permissions.canLabelSources ? handleAuthorizeProvider : undefined}
                  enforcementReadiness={providerDailyDetail[group.key]?.length > 0 ? computeEnforcementReadiness(providerDailyDetail[group.key]) : null}
                />
              )}

              {/* Provider-level health card */}
              {viewMode === "grouped" && expandedGroups.has(group.key) && (
                <div className="mt-2 ml-4">
                  {loadingProviderStats.has(group.key) ? (
                    <div className="py-3 flex items-center justify-center text-slate-400 text-sm">
                      Loading daily breakdown...
                    </div>
                  ) : providerDailyDetail[group.key] && providerDailyDetail[group.key].length > 0 ? (
                    <ProviderHealthCard dailyDetail={providerDailyDetail[group.key]} providerName={group.providerName} />
                  ) : null}
                </div>
              )}

              {/* Sources (expanded) */}
              {(viewMode !== "grouped" || expandedGroups.has(group.key)) && (
                <>
                  {/* Mobile Cards View */}
                  <div className={`md:hidden space-y-2 ${viewMode === "grouped" ? "mt-2 ml-4" : ""}`}>
                    {group.sources.map((source) => (
                      <ExpandableSourceCard
                        key={source.id}
                        source={source}
                        permissions={permissions}
                        expanded={expandedSources.has(source.id)}
                        onToggleExpand={() => handleToggleExpand(source.id)}
                        selected={selectedSources.has(source.id)}
                        onToggleSelect={() => handleToggleSelect(source.id)}
                        dailyStats={sourceStats[source.id] || null}
                        loadingStats={loadingStats.has(source.id)}
                        metadata={sourceMetadata[source.id] || null}
                        onLabelClick={() => handleLabelClick(source)}
                        onUpgradeClick={() => setUpgradePromptFeature("hostname_view")}
                        showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                      />
                    ))}
                  </div>

                  {/* Desktop Table View */}
                  <div className={`hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden ${viewMode === "grouped" ? "mt-2 ml-4" : ""}`}>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            {(selectedSources.size > 0 || permissions.canViewHostname) && (
                              <th className="w-10 px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={group.sources.every((s) => selectedSources.has(s.id))}
                                  onChange={(e) => handleSelectGroup(group.sources, e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                                />
                              </th>
                            )}
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Source
                            </th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Domain
                            </th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Volume
                            </th>
                            <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              SPF
                            </th>
                            <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              DKIM
                            </th>
                            <th className="px-6 py-3"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {group.sources.map((source) => (
                            <ExpandableSourceRow
                              key={source.id}
                              source={source}
                              permissions={permissions}
                              expanded={expandedSources.has(source.id)}
                              onToggleExpand={() => handleToggleExpand(source.id)}
                              selected={selectedSources.has(source.id)}
                              onToggleSelect={() => handleToggleSelect(source.id)}
                              dailyStats={sourceStats[source.id] || null}
                              loadingStats={loadingStats.has(source.id)}
                              metadata={sourceMetadata[source.id] || null}
                              onLabelClick={() => handleLabelClick(source)}
                              onUpgradeClick={() => setUpgradePromptFeature("hostname_view")}
                              showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Forwarders Section */}
          {forwarderSources.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Forward className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-semibold text-slate-800">Forwarders & Mailing Lists</h2>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  {forwarderSources.length}
                </span>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                These sources forward email on behalf of your senders. SPF alignment failures are expected.
              </p>

              {forwarderGroups.map((group) => (
                <div key={`forwarder-${group.key}`} className="mb-3">
                  {/* Forwarder Group Header */}
                  {viewMode === "grouped" && (
                    <SourceProviderGroup
                      providerName={group.providerName}
                      providerId={group.providerId}
                      providerCategory={group.providerCategory}
                      effectiveStatus={group.effectiveStatus}
                      sources={group.sources}
                      expanded={expandedGroups.has(`forwarder-${group.key}`)}
                      onToggle={() => handleToggleGroup(`forwarder-${group.key}`, group.sources)}
                      selectedCount={group.sources.filter((s) => selectedSources.has(s.id)).length}
                      onSelectAll={(selected) => handleSelectGroup(group.sources, selected)}
                      showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                      onAuthorize={permissions.canLabelSources ? handleAuthorizeProvider : undefined}
                      enforcementReadiness={providerDailyDetail[`forwarder-${group.key}`]?.length > 0 ? computeEnforcementReadiness(providerDailyDetail[`forwarder-${group.key}`]) : null}
                    />
                  )}

                  {/* Forwarder provider-level health card */}
                  {viewMode === "grouped" && expandedGroups.has(`forwarder-${group.key}`) && (
                    <div className="mt-2 ml-4">
                      {loadingProviderStats.has(`forwarder-${group.key}`) ? (
                        <div className="py-3 flex items-center justify-center text-slate-400 text-sm">
                          Loading daily breakdown...
                        </div>
                      ) : providerDailyDetail[`forwarder-${group.key}`] && providerDailyDetail[`forwarder-${group.key}`].length > 0 ? (
                        <ProviderHealthCard dailyDetail={providerDailyDetail[`forwarder-${group.key}`]} providerName={group.providerName} />
                      ) : null}
                    </div>
                  )}

                  {/* Forwarder Sources (expanded) */}
                  {(viewMode !== "grouped" || expandedGroups.has(`forwarder-${group.key}`)) && (
                    <>
                      {/* Mobile Cards View */}
                      <div className={`md:hidden space-y-2 ${viewMode === "grouped" ? "mt-2 ml-4" : ""}`}>
                        {group.sources.map((source) => (
                          <ExpandableSourceCard
                            key={source.id}
                            source={source}
                            permissions={permissions}
                            expanded={expandedSources.has(source.id)}
                            onToggleExpand={() => handleToggleExpand(source.id)}
                            selected={selectedSources.has(source.id)}
                            onToggleSelect={() => handleToggleSelect(source.id)}
                            dailyStats={sourceStats[source.id] || null}
                            loadingStats={loadingStats.has(source.id)}
                            metadata={sourceMetadata[source.id] || null}
                            onLabelClick={() => handleLabelClick(source)}
                            onUpgradeClick={() => setUpgradePromptFeature("hostname_view")}
                            showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                          />
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <div className={`hidden md:block bg-white rounded-xl border border-blue-200 overflow-hidden ${viewMode === "grouped" ? "mt-2 ml-4" : ""}`}>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-blue-100 bg-blue-50">
                                {(selectedSources.size > 0 || permissions.canViewHostname) && (
                                  <th className="w-10 px-4 py-3">
                                    <input
                                      type="checkbox"
                                      checked={group.sources.every((s) => selectedSources.has(s.id))}
                                      onChange={(e) => handleSelectGroup(group.sources, e.target.checked)}
                                      className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                                    />
                                  </th>
                                )}
                                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                  Source
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                  Domain
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                  Volume
                                </th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                  SPF
                                </th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                  DKIM
                                </th>
                                <th className="px-6 py-3"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {group.sources.map((source) => (
                                <ExpandableSourceRow
                                  key={source.id}
                                  source={source}
                                  permissions={permissions}
                                  expanded={expandedSources.has(source.id)}
                                  onToggleExpand={() => handleToggleExpand(source.id)}
                                  selected={selectedSources.has(source.id)}
                                  onToggleSelect={() => handleToggleSelect(source.id)}
                                  dailyStats={sourceStats[source.id] || null}
                                  loadingStats={loadingStats.has(source.id)}
                                  metadata={sourceMetadata[source.id] || null}
                                  onLabelClick={() => handleLabelClick(source)}
                                  onUpgradeClick={() => setUpgradePromptFeature("hostname_view")}
                                  showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Unidentified Sources Section */}
          {allUnknownSources.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-slate-800">Unidentified Sources</h2>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                  {allUnknownSources.length}
                </span>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                These IPs could not be matched to a known provider. Label them to track remediation status and generate tasks.
              </p>

              {/* Desktop Table View */}
              <div className="hidden md:block bg-white rounded-xl border border-amber-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-amber-100 bg-amber-50">
                        {(selectedSources.size > 0 || permissions.canViewHostname) && (
                          <th className="w-10 px-4 py-3">
                            <input
                              type="checkbox"
                              checked={allUnknownSources.every((s) => selectedSources.has(s.id))}
                              onChange={(e) => handleSelectGroup(allUnknownSources, e.target.checked)}
                              className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                            />
                          </th>
                        )}
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          IP Address
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Domain
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Volume
                        </th>
                        <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          SPF
                        </th>
                        <th className="text-right px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          DKIM
                        </th>
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {allUnknownSources.map((source) => (
                        <ExpandableSourceRow
                          key={source.id}
                          source={source}
                          permissions={permissions}
                          expanded={expandedSources.has(source.id)}
                          onToggleExpand={() => handleToggleExpand(source.id)}
                          selected={selectedSources.has(source.id)}
                          onToggleSelect={() => handleToggleSelect(source.id)}
                          dailyStats={sourceStats[source.id] || null}
                          loadingStats={loadingStats.has(source.id)}
                          metadata={sourceMetadata[source.id] || null}
                          onLabelClick={() => handleLabelClick(source)}
                          onUpgradeClick={() => setUpgradePromptFeature("hostname_view")}
                          showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Cards View */}
              <div className="md:hidden space-y-2">
                {allUnknownSources.map((source) => (
                  <ExpandableSourceCard
                    key={source.id}
                    source={source}
                    permissions={permissions}
                    expanded={expandedSources.has(source.id)}
                    onToggleExpand={() => handleToggleExpand(source.id)}
                    selected={selectedSources.has(source.id)}
                    onToggleSelect={() => handleToggleSelect(source.id)}
                    dailyStats={sourceStats[source.id] || null}
                    loadingStats={loadingStats.has(source.id)}
                    metadata={sourceMetadata[source.id] || null}
                    onLabelClick={() => handleLabelClick(source)}
                    onUpgradeClick={() => setUpgradePromptFeature("hostname_view")}
                    showCheckbox={selectedSources.size > 0 || permissions.canViewHostname}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Bulk Export Bar */}
      <BulkExportBar
        selectedCount={selectedSources.size}
        onExport={handleExport}
        onLabel={handleBulkLabelClick}
        onClearSelection={() => setSelectedSources(new Set())}
        canLabel={permissions.canLabelSources}
      />

      {/* Label Dialog */}
      {labelDialogSource && (
        <SourceLabelDialog
          source={labelDialogSource}
          tier={tier}
          onLabel={handleLabelSource}
          onClose={() => setLabelDialogSource(null)}
        />
      )}

      {/* Bulk Label Dialog */}
      {showBulkLabelDialog && (
        <BulkLabelDialog
          sourceCount={selectedSources.size}
          onLabel={handleBulkLabel}
          onClose={() => setShowBulkLabelDialog(false)}
        />
      )}

      {/* Auto-Label Confirmation Dialog */}
      <Modal
        open={showAutoLabelConfirm && !!autoLabelPreview}
        onOpenChange={(open) => {
          if (!open) {
            setShowAutoLabelConfirm(false);
            setAutoLabelPreview(null);
          }
        }}
      >
        <Modal.Content title="Auto-Label Sources" size="md">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Intelligent source classification</p>
              </div>
            </div>

            {autoLabelPreview && (
              <>
                <div className="bg-slate-50 rounded-lg p-4 mb-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total sources</span>
                    <span className="font-medium text-slate-800">{autoLabelPreview.totalSources}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Unlabeled sources</span>
                    <span className="font-medium text-amber-600">{autoLabelPreview.unlabeledSources}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">With known provider</span>
                    <span className="font-medium text-emerald-700">{autoLabelPreview.withKnownProvider}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Detected forwarders</span>
                    <span className="font-medium text-blue-500">{autoLabelPreview.detectedForwarders}</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-slate-700">Estimated auto-labeled</span>
                      <span className="text-purple-600">{autoLabelPreview.estimatedAutoLabeled}</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-slate-600 mb-4">
                  This will analyze unlabeled sources and automatically classify them based on:
                </p>
                <ul className="text-sm text-slate-600 mb-4 space-y-1 ml-4 list-disc">
                  <li>Known provider (Google, Microsoft, etc.) &rarr; <span className="text-emerald-700">Authorized</span></li>
                  <li>Detected forwarders &rarr; <span className="text-blue-500">Forwarder</span></li>
                  <li>Unknown with failing auth &rarr; <span className="text-purple-600">Suspicious</span></li>
                  <li>Ambiguous cases &rarr; <span className="text-amber-600">Pending Review</span></li>
                </ul>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAutoLabelConfirm(false);
                      setAutoLabelPreview(null);
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAutoLabel}
                    disabled={autoLabeling}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-600 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Wand2 className="w-4 h-4" />
                    {autoLabeling ? "Analyzing..." : "Auto-Label Now"}
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal.Content>
      </Modal>

      {/* Upgrade Prompt */}
      {upgradePromptFeature && (
        <UpgradePrompt
          feature={upgradePromptFeature}
          currentTier={tier}
          requiredTier="starter"
          onUpgrade={handleUpgrade}
          onDismiss={() => setUpgradePromptFeature(null)}
          location="sources_page"
          severity={
            sources.length > 20 ? "high" : sources.length > 5 ? "medium" : "low"
          }
          dynamicVars={{ sourceCount: sources.length }}
        />
      )}
    </div>
  );
}

interface SourceWithDomain extends Source {
  domainName?: string;
}

function ExpandableSourceRow({
  source,
  permissions,
  expanded,
  onToggleExpand,
  selected,
  onToggleSelect,
  dailyStats,
  loadingStats,
  metadata,
  onLabelClick,
  onUpgradeClick,
  showCheckbox,
}: {
  source: SourceWithDomain;
  permissions: Permissions;
  expanded: boolean;
  onToggleExpand: () => void;
  selected: boolean;
  onToggleSelect: () => void;
  dailyStats: SourceDailyStat[] | null;
  loadingStats: boolean;
  metadata?: { dkim_selectors: string[]; envelope_from_domains: string[] } | null;
  onLabelClick: () => void;
  onUpgradeClick: () => void;
  showCheckbox: boolean;
}) {
  const totalCount = source.total_message_count || 0;
  const spfPassRate = Math.round((source.spf_pass_rate || 0) * 100);
  // For forwarders, show DKIM auth rate instead of alignment rate
  // (DKIM alignment always fails for forwarders because they modify messages)
  const sourceIsForwarder = isForwarder(source);
  const dkimPassRate = sourceIsForwarder && source.dkim_auth_pass_rate != null
    ? Math.round(source.dkim_auth_pass_rate * 100)
    : Math.round((source.dkim_pass_rate || 0) * 100);

  const canSeeDetails = permissions.canViewHostname && !source._tier_restricted;

  const primaryDisplay = canSeeDetails && source.ptr_hostname
    ? source.ptr_hostname
    : source.source_ip || source.identifier;

  const secondaryDisplay =
    canSeeDetails && source.ptr_hostname
      ? source.source_ip || source.identifier
      : null;

  const orgDisplay = canSeeDetails
    ? source.org_name || source.provider_name || "Unknown"
    : null;

  const labelDisplay = source.label_category
    ? formatLabel(source.label_category, source.label_subtype)
    : null;

  return (
    <>
      <tr
        className={`hover:bg-slate-50 transition-colors cursor-pointer ${expanded ? "bg-slate-50" : ""}`}
        onClick={onToggleExpand}
      >
        {showCheckbox && (
          <td className="w-10 px-4 py-4" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
            />
          </td>
        )}
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <button className="text-slate-400 hover:text-slate-600">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <div>
              <p className="font-medium text-slate-800">{primaryDisplay}</p>
              {secondaryDisplay && (
                <p className="font-mono text-xs text-slate-400">{secondaryDisplay}</p>
              )}
              {orgDisplay ? (
                <p className="text-sm text-slate-500">{orgDisplay}</p>
              ) : source._tier_restricted ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpgradeClick();
                  }}
                  className="text-sm text-slate-400 hover:text-amber-600 flex items-center gap-1"
                >
                  <Lock className="w-3 h-3" />
                  Upgrade to see details
                </button>
              ) : null}
            </div>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-600">
              {source.domainName || source.domain_name || "—"}
            </span>
          </div>
        </td>
        <td className="px-6 py-4">
          <div className="flex flex-col gap-1">
            <StatusBadge status={getSourceStatus(source)} />
            <AIClassificationBadge source={source} />
            {labelDisplay && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs w-fit">
                <Tag className="w-3 h-3" />
                {labelDisplay}
              </span>
            )}
          </div>
        </td>
        <td className="px-6 py-4 text-slate-600">
          {totalCount.toLocaleString()}
        </td>
        <td className="px-6 py-4 text-right">
          <span className={`text-sm font-medium ${
            spfPassRate >= 80 ? "text-emerald-700" : spfPassRate >= 50 ? "text-amber-600" : "text-red-600"
          }`}>
            {spfPassRate}%
          </span>
        </td>
        <td className="px-6 py-4 text-right">
          <span className={`text-sm font-medium ${
            dkimPassRate >= 80 ? "text-emerald-700" : dkimPassRate >= 50 ? "text-amber-600" : "text-red-600"
          }`}>
            {dkimPassRate}%
          </span>
        </td>
        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
          {permissions.canLabelSources ? (
            <button
              onClick={onLabelClick}
              className="px-3 py-1.5 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-colors flex items-center gap-1"
            >
              <Tag className="w-4 h-4" />
              {source.label_category ? "Edit" : "Label"}
            </button>
          ) : (
            <LockedFeatureButton
              label="Label"
              feature="source_labeling"
              onClick={onLabelClick}
            />
          )}
        </td>
      </tr>
      {/* Expanded Details Row */}
      {expanded && (
        <tr>
          <td colSpan={showCheckbox ? 8 : 7} className="bg-slate-50 border-t border-slate-100">
            <SourceDetailsPanel
              source={source}
              dailyStats={dailyStats}
              loading={loadingStats}
              metadata={metadata}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// Mobile card component for expandable sources
function ExpandableSourceCard({
  source,
  permissions,
  expanded,
  onToggleExpand,
  selected,
  onToggleSelect,
  dailyStats,
  loadingStats,
  metadata,
  onLabelClick,
  onUpgradeClick,
  showCheckbox,
}: {
  source: SourceWithDomain;
  permissions: Permissions;
  expanded: boolean;
  onToggleExpand: () => void;
  selected: boolean;
  onToggleSelect: () => void;
  dailyStats: SourceDailyStat[] | null;
  loadingStats: boolean;
  metadata?: { dkim_selectors: string[]; envelope_from_domains: string[] } | null;
  onLabelClick: () => void;
  onUpgradeClick: () => void;
  showCheckbox: boolean;
}) {
  const totalCount = source.total_message_count || 0;
  const spfPassRate = Math.round((source.spf_pass_rate || 0) * 100);
  // For forwarders, show DKIM auth rate instead of alignment rate
  const sourceIsForwarder = isForwarder(source);
  const dkimPassRate = sourceIsForwarder && source.dkim_auth_pass_rate != null
    ? Math.round(source.dkim_auth_pass_rate * 100)
    : Math.round((source.dkim_pass_rate || 0) * 100);

  const canSeeDetails = permissions.canViewHostname && !source._tier_restricted;
  const primaryDisplay = canSeeDetails && source.ptr_hostname
    ? source.ptr_hostname
    : source.source_ip || source.identifier;
  const orgDisplay = canSeeDetails
    ? source.org_name || source.provider_name || null
    : null;
  const labelDisplay = source.label_category
    ? formatLabel(source.label_category, source.label_subtype)
    : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Card Header - Tap to expand */}
      <div
        className={`p-3 ${expanded ? "bg-slate-50" : ""}`}
        onClick={onToggleExpand}
      >
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          {showCheckbox && (
            <div onClick={(e) => e.stopPropagation()} className="pt-1">
              <input
                type="checkbox"
                checked={selected}
                onChange={onToggleSelect}
                className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
              />
            </div>
          )}

          {/* Expand chevron */}
          <div className="pt-1 text-slate-400">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <StatusBadge status={getSourceStatus(source)} />
              <AIClassificationBadge source={source} />
              {labelDisplay && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                  <Tag className="w-3 h-3" />
                  {labelDisplay}
                </span>
              )}
            </div>

            <p className="font-medium text-slate-800 text-sm truncate">{primaryDisplay}</p>

            <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
              {orgDisplay && <span className="truncate">{orgDisplay}</span>}
              {orgDisplay && <span>•</span>}
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {source.domainName || source.domain_name || "—"}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="text-right flex-shrink-0">
            <div className="flex items-center gap-2 justify-end mb-1">
              <div className="text-xs">
                <span className="text-slate-500">SPF </span>
                <span className={`font-medium ${
                  spfPassRate >= 80 ? "text-emerald-700" : spfPassRate >= 50 ? "text-amber-600" : "text-red-600"
                }`}>
                  {spfPassRate}%
                </span>
              </div>
              <div className="text-xs">
                <span className="text-slate-500">DKIM </span>
                <span className={`font-medium ${
                  dkimPassRate >= 80 ? "text-emerald-700" : dkimPassRate >= 50 ? "text-amber-600" : "text-red-600"
                }`}>
                  {dkimPassRate}%
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              {totalCount.toLocaleString()} msgs
            </p>
          </div>
        </div>

        {/* Upgrade prompt for locked sources */}
        {source._tier_restricted && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUpgradeClick();
            }}
            className="mt-2 text-xs text-slate-400 hover:text-amber-600 flex items-center gap-1"
          >
            <Lock className="w-3 h-3" />
            Upgrade to see details
          </button>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-slate-100">
          <SourceDetailsPanel
            source={source}
            dailyStats={dailyStats}
            loading={loadingStats}
            metadata={metadata}
          />

          {/* Label button */}
          <div className="px-3 pb-3">
            {permissions.canLabelSources ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onLabelClick();
                }}
                className="w-full px-4 py-2 text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Tag className="w-4 h-4" />
                {source.label_category ? "Edit Label" : "Label Source"}
              </button>
            ) : (
              <LockedFeatureButton
                label="Label Source"
                feature="source_labeling"
                onClick={onLabelClick}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatLabel(category: string, subtype?: string): string {
  const categoryNames: Record<string, string> = {
    authorized: "Authorized",
    customer_support: "Support",
    cicd: "CI/CD",
    forwarder: "Forwarder",
    unauthorized: "Unauthorized",
    investigating: "Investigating",
  };

  const subtypeNames: Record<string, string> = {
    transactional: "Transactional",
    marketing: "Marketing",
    corporate: "Corporate",
    infrastructure: "Infrastructure",
    sso_identity: "SSO/Identity",
    helpdesk: "Helpdesk",
    security_alerts: "Alerts",
    mailing_list: "Mailing List",
    malicious: "Malicious",
    unknown: "Unknown",
  };

  if (subtype && subtypeNames[subtype]) {
    return subtypeNames[subtype];
  }
  return categoryNames[category] || category;
}